import {
  FC,
  Ref,
  forwardRef,
  PropsWithoutRef,
  ChangeEvent,
  useCallback,
  useState,
  ChangeEventHandler,
  useEffect
} from 'react';

import { ForwardProps, NoChildrenProp } from '../../types';
import FormField from '../FormField';
import { FormControlProps, StyledFormControl } from '../FormControl';
import { hasProp } from '../../utils';
import { useAutoResize, useConsolidatedRef, useI18n, useUID } from '../../hooks';
import Text from '../Text';

import StyledTextArea from './TextArea.styles';

export interface TextAreaProps extends FormControlProps, NoChildrenProp {
  /** Minimum length of characters that can be input. */
  minLength?: number;
  /** Maximum length of characters that can be input. */
  maxLength?: number;
  /**
   * Allows the user to resize the Text Area.
   * @default false
   */
  resizable?: boolean;
  /**
   * Enables the Text Area to resize itself automatically.
   * @default false
   */
  autoResize?: boolean;
  /**
   * Display a live character count in relation to the maxLength.
   * @default false
   */
  displayCharCount?: boolean;
  /**
   * Allow or disallow a value beyond the maxLength.
   * @default true
   */
  hardStop?: boolean;
  onChange?: ChangeEventHandler<HTMLTextAreaElement>;
}

const TextArea: FC<TextAreaProps & ForwardProps> = forwardRef(
  (props: PropsWithoutRef<TextAreaProps>, ref: Ref<HTMLTextAreaElement>) => {
    const uid = useUID();
    const {
      id = uid,
      value,
      defaultValue,
      required = false,
      disabled = false,
      label,
      labelHidden,
      info,
      status,
      resizable = false,
      autoResize = false,
      maxLength,
      displayCharCount = false,
      hardStop = true,
      onChange: onChangeProp,
      ...restProps
    } = props;

    const t = useI18n();

    const [textAreaRef, resizeTextArea] = useAutoResize<HTMLTextAreaElement>(undefined, 100);
    const consolidatedRef = useConsolidatedRef(textAreaRef, ref);
    const [charCount, setCharCount] = useState(0);

    const controlProp: {
      value?: string;
      defaultValue?: string;
    } = {};

    useEffect(() => {
      if (hasProp(props, 'value')) {
        setCharCount(props.value ? props.value.length : 0);
      } else if (hasProp(props, 'defaultValue')) {
        setCharCount(props.defaultValue ? props.defaultValue.length : 0);
      }
    }, [props]);

    // Conditionally render component as controlled/uncontrolled
    if (hasProp(props, 'value')) {
      controlProp.value = value ?? '';
    } else if (hasProp(props, 'defaultValue')) {
      controlProp.defaultValue = defaultValue ?? '';
    }

    const onChange = useCallback(
      (e: ChangeEvent<HTMLTextAreaElement>) => {
        if (!hardStop || maxLength === undefined || e.target.value.length <= maxLength) {
          onChangeProp?.(e);
          setCharCount(e.target.value.length);
          resizeTextArea();
        }
      },
      [onChangeProp, resizeTextArea, hardStop, maxLength]
    );

    const Comp = (
      <StyledFormControl
        {...{
          ref: autoResize ? consolidatedRef : ref,
          id,
          required,
          disabled,
          status,
          resizable,
          maxLength: hardStop ? maxLength : undefined,
          ...controlProp,
          ...restProps,
          onChange,
          as: StyledTextArea
        }}
      />
    );

    return label || displayCharCount ? (
      <FormField
        {...{
          label,
          labelHidden,
          id,
          info,
          status,
          charLimitDisplay:
            displayCharCount && typeof maxLength === 'number' ? (
              <Text
                variant='secondary'
                aria-live={maxLength >= 0 && maxLength - charCount <= 20 ? 'polite' : 'off'}
              >
                {maxLength >= 0 ? t('x_of_y', [charCount || '0', maxLength]) : charCount}
              </Text>
            ) : undefined,
          required,
          disabled
        }}
      >
        {Comp}
      </FormField>
    ) : (
      Comp
    );
  }
);

export default TextArea;
